import React, { useEffect, useState } from "react";

/**
 * DegradedModeBanner - Shows when app is in degraded mode with retry capability
 * 
 * Features:
 * - Shows reason for degraded state
 * - Auto-retry countdown timer
 * - Manual "Retry now" button
 * - Doesn't block the app, just informs user
 */
export default function DegradedModeBanner({
  degraded,
  reason,
  lastError,
  nextRetryAt,
  onRetry,
}) {
  const [seconds, setSeconds] = useState(null);

  useEffect(() => {
    if (!nextRetryAt) return setSeconds(null);

    const tick = () => {
      const ms = nextRetryAt - Date.now();
      setSeconds(ms > 0 ? Math.ceil(ms / 1000) : 0);
    };

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [nextRetryAt]);

  if (!degraded) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[2147483647] px-3 py-2 bg-amber-950/95 border-b border-amber-500/30">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-amber-200 font-semibold">
            Degraded mode
            {reason ? <span className="text-amber-300 font-normal"> — {reason}</span> : null}
          </div>
          {lastError ? (
            <div className="text-xs text-amber-200/80 truncate">
              {String(lastError)}
            </div>
          ) : (
            <div className="text-xs text-amber-200/80">
              Some services are unavailable. App should still work.
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {typeof seconds === "number" ? (
            <span className="text-xs text-amber-200/70">
              retry in {seconds}s
            </span>
          ) : null}
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-1.5 rounded-full bg-amber-500/90 hover:bg-amber-400 text-black text-xs font-semibold"
          >
            Retry now
          </button>
        </div>
      </div>
    </div>
  );
}
